import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { FinanceModel, PeripheralFinanceModel } from '../api/contracts/financemodels';
import {
  useCompanySelectedProducts,
  useCustomerSelectedFinanceModels,
  useUpdateCompanySelectedProducts,
  useUpdateCustomerSelectedFinanceModels
} from '../api/hooks/companies';
import { useFinanceModels, usePeripheralFinanceModels } from '../api/hooks/financemodels';
import { useUpsertAdditionalBundleStock, useUpsertAdditionalGeneralStock } from '../api/hooks/stock/additionalStock';
import {
  useCreateStockLevel,
  useStockLevels,
  useUpdateStockLevel,
  useUpdateStockLevelStatus
} from '../api/hooks/stock/stockLevels';
import { useCreateBundleWeighting, useUpdateBundleWeighting } from '../api/hooks/stock/weighting';
import { useRole } from '../authentication/hooks/user';
import {
  AdditionalStock,
  CustomerCatalogueContext,
  CustomerCatalogueInfo,
  CustomerCatalogueProps,
  StockBundleWeighting,
  StockLevelStatus,
  StockParameters
} from './useCustomerCatalogueService';

export const CustomerCatalogueProvider: FC<PropsWithChildren<CustomerCatalogueProps>> = ({
  customerId,
  children,
  customPartnerId
}) => {
  const myPartnerId = useRole().partnerId;
  const partnerId = customPartnerId || myPartnerId;

  if (!partnerId) {
    throw Error('Could not get partner ID');
  }

  const selectedProducts = useCompanySelectedProducts(customerId, partnerId);
  const updateProducts = useUpdateCompanySelectedProducts(customerId, partnerId);

  const [stockLevels] = useStockLevels(partnerId, customerId);
  const createStockLevel = useCreateStockLevel();
  const updateStockLevel = useUpdateStockLevel();

  useEffect(() => {
    if (stockLevels.length === 0) {
      createStockLevel.mutateAsync({
        employeeCount: stockParameters.numberOfEmployees!,
        churnRate: stockParameters.churnRatePercentage!,
        growthRate: stockParameters.gsdPercentage!,
        mspId: partnerId,
        stockLevelState: StockLevelStatus.DRAFT,
        customerId
      }).catch(console.error);
    }
  }, [customerId, partnerId]);

  const createBundleWeighting = useCreateBundleWeighting();
  const updateBundleWeighting = useUpdateBundleWeighting();

  const updateStockLevelStatus = useUpdateStockLevelStatus();

  const upsertAdditionalBundleStock = useUpsertAdditionalBundleStock(partnerId, customerId);
  const upsertAdditionalGeneralStock = useUpsertAdditionalGeneralStock(partnerId, customerId);

  const [selectedFinanceModelsForCustomer] = useCustomerSelectedFinanceModels(customerId, partnerId);
  const updateSelectedFinanceModels = useUpdateCustomerSelectedFinanceModels(customerId, partnerId);

  const financeModels = useFinanceModels();
  const peripheralFinanceModels = usePeripheralFinanceModels();

  const selectedModels = financeModels.filter(model =>
    selectedFinanceModelsForCustomer.financeModelIds.includes(model.id)
  );
  const selectedPeripheralModel = peripheralFinanceModels.find(
    model => selectedFinanceModelsForCustomer.peripheralFinanceModelId === model.id
  );

  const stockParameters: StockParameters = useMemo(() => {
    if (stockLevels.length === 0)
      return {
        numberOfEmployees: 0,
        churnRatePercentage: 0,
        gsdPercentage: 0
      };

    return {
      numberOfEmployees: stockLevels[0].employeeCount,
      churnRatePercentage: stockLevels[0].churnRate,
      gsdPercentage: stockLevels[0].growthRate
    };
  }, [stockLevels]);

  const stockLevelState: StockLevelStatus = stockLevels[0]?.stockLevelState || StockLevelStatus.DRAFT;

  const bundleWeightings: StockBundleWeighting[] = useMemo(() => {
    if (stockLevels.length === 0) return [];

    return stockLevels[0].bundleWeightings.map(weighting => {
      return {
        id: weighting.id,
        bundleId: weighting.customerBundleId,
        weightingPercentage: weighting.weighting
      };
    });
  }, [stockLevels]);

  const additionalBundleStockLevels: AdditionalStock[] = useMemo(() => {
    if (stockLevels.length === 0) return [];

    return stockLevels[0].additionalBundleStocks?.map(stock => {
      return {
        productId: stock.catalogItemId,
        minimumStockLevel: stock.minimumStockLevel,
        maximumStockLevel: stock.maximumStockLevel
      };
    });
  }, [stockLevels]);

  const additionalGeneralStockLevels: AdditionalStock[] = useMemo(() => {
    if (stockLevels.length === 0) return [];

    return stockLevels[0].additionalGeneralStocks?.map(stock => {
      return {
        productId: stock.catalogItemId,
        minimumStockLevel: stock.minimumStockLevel,
        maximumStockLevel: stock.maximumStockLevel
      };
    });
  }, [stockLevels]);

  const store = useMemo(
    () =>
      create<CustomerCatalogueInfo>((set, get) => ({
        selectedProductIds: selectedProducts || [],
        selectProduct: (productId: string) => {
          const newIds = [...get().selectedProductIds];

          if (newIds.includes(productId)) {
            const index = newIds.findIndex(p => p === productId);
            newIds.splice(index, 1);
          } else {
            newIds.push(productId);
          }

          set({ selectedProductIds: newIds });
          void updateProducts.mutateAsync(newIds);
        },
        selectedFinanceModels: selectedModels,
        selectedPeripheralFinanceModel: selectedPeripheralModel,
        selectCapitalPurchaseForMainDevice: async (categoryId: string, manufacturerId: string) => {
          const selectedFinanceModels = [...get().selectedFinanceModels];

          const financeModelsToKeep = selectedFinanceModels.filter(
            m => !(m.categoryId === categoryId && m.manufacturerId === manufacturerId)
          );

          const payload = {
            financeModelIds: financeModelsToKeep.map(model => model.id),
            peripheralFinanceModelId: get().selectedPeripheralFinanceModel?.id
          };

          await updateSelectedFinanceModels.mutateAsync(payload);

          set({ selectedFinanceModels: financeModelsToKeep });
        },
        selectCapitalPurchaseForPeripheral: async () => {
          const payload = {
            financeModelIds: get().selectedFinanceModels.map(model => model.id),
            peripheralFinanceModelId: undefined
          };

          await updateSelectedFinanceModels.mutateAsync(payload);

          set({ selectedPeripheralFinanceModel: undefined });
        },
        selectFinanceModel: async (model: FinanceModel) => {
          let selectedFinanceModels = [...get().selectedFinanceModels];

          const financeModel = selectedFinanceModels.find(
            m => m.categoryId === model.categoryId && m.manufacturerId === model.manufacturerId
          );

          if (financeModel) {
            selectedFinanceModels = selectedFinanceModels.filter(m => m.id !== financeModel.id);
          }

          selectedFinanceModels.push(model);

          const payload = {
            financeModelIds: selectedFinanceModels.map(model => model.id),
            peripheralFinanceModelId: get().selectedPeripheralFinanceModel?.id
          };

          await updateSelectedFinanceModels.mutateAsync(payload);

          set({ selectedFinanceModels: selectedFinanceModels });
        },
        selectPeripheralFinanceModel: async (model: PeripheralFinanceModel) => {
          const payload = {
            financeModelIds: get().selectedFinanceModels.map(model => model.id),
            peripheralFinanceModelId: model.id
          };

          await updateSelectedFinanceModels.mutateAsync(payload);

          set({ selectedPeripheralFinanceModel: model });
        },
        stockParameters,
        bundleWeightings,
        additionalBundleStockLevels,
        additionalGeneralStockLevels,
        status: stockLevelState,
        setStockParameters: (newParams: StockParameters) => {
          set({ stockParameters: newParams });
        },
        saveStockParameters: async (submit?: boolean) => {
          const stockParameters = get().stockParameters;

          const status = submit ? StockLevelStatus.APPROVED : StockLevelStatus.DRAFT;

          const payload = {
            employeeCount: stockParameters.numberOfEmployees!,
            churnRate: stockParameters.churnRatePercentage!,
            growthRate: stockParameters.gsdPercentage!,
            stockLevelState: status,
            mspId: partnerId,
            customerId
          };

          await updateStockLevel.mutateAsync({
            ...payload,
            id: stockLevels[0].id
          });

          set({ status: status });
        },
        setBundleWeightings: (newArray: StockBundleWeighting[]) => {
          set({ bundleWeightings: newArray });
        },
        setBundleWeighting: (bundleId: string, weighting: number = 0) => {
          const newWeightings = [...get().bundleWeightings];

          const weightingObj = newWeightings.find(w => w.bundleId === bundleId);
          if (!weightingObj) throw new Error('Missing weighting');

          weightingObj.weightingPercentage = weighting;
          set({ bundleWeightings: newWeightings });
        },
        saveBundleWeighting: async (bundleId: string) => {
          const newWeightings = [...get().bundleWeightings];

          const weightingObj = newWeightings.find(w => w.bundleId === bundleId);
          if (!weightingObj) throw new Error('Missing weighting');

          const payload = {
            customerBundleId: bundleId,
            weighting: weightingObj.weightingPercentage,
            mspId: partnerId,
            customerId,
            state: StockLevelStatus.DRAFT,
            stockLevelId: stockLevels[0].id
          };

          if (weightingObj?.id) {
            await updateBundleWeighting.mutateAsync({ ...payload, id: weightingObj.id });
          } else {
            const response = await createBundleWeighting.mutateAsync(payload);
            weightingObj.id = response.id;
          }
          set({ bundleWeightings: newWeightings, status: StockLevelStatus.DRAFT });
        },
        setAdditionalBundleStockLevels: (newArray: AdditionalStock[]) => {
          set({ additionalBundleStockLevels: newArray });
        },
        setAdditionalBundleStock: (productId: string, minimumStockLevel: number, maximumStockLevel: number) => {
          const newStockLevels = [...get().additionalBundleStockLevels];

          const stockObj = newStockLevels.find(s => s.productId === productId);

          if (stockObj) {
            stockObj.minimumStockLevel = minimumStockLevel;
            stockObj.maximumStockLevel = maximumStockLevel;
          } else {
            newStockLevels.push({ productId, minimumStockLevel, maximumStockLevel });
          }

          set({ additionalBundleStockLevels: newStockLevels });
        },
        saveAdditionalBundleStock: async (productIds: string[]) => {
          const newStockLevels = [...get().additionalBundleStockLevels];

          const stockObjs = newStockLevels.filter(s => productIds.includes(s.productId));
          if (!stockObjs) throw new Error('Missing additional bundle stock objects');

          const payload = stockObjs.map(s => {
            return {
              catalogItemId: s.productId,
              minimumStockLevel: s.minimumStockLevel,
              maximumStockLevel: s.maximumStockLevel,
              stockLevelId: stockLevels[0].id
            };
          });

          await upsertAdditionalBundleStock.mutateAsync(payload);
          await updateStockLevelStatus.mutateAsync({
            mspId: partnerId,
            customerId,
            id: stockLevels[0].id,
            state: StockLevelStatus.DRAFT
          });
          set({ additionalBundleStockLevels: newStockLevels, status: StockLevelStatus.DRAFT });
        },
        setAdditionalGeneralStockLevels: (newArray: AdditionalStock[]) => {
          set({ additionalGeneralStockLevels: newArray });
        },
        setAdditionalGeneralStock: (productId: string, minimumStockLevel: number, maximumStockLevel: number) => {
          const newStockLevels = [...get().additionalGeneralStockLevels];

          const stockObj = newStockLevels.find(s => s.productId === productId);

          if (stockObj) {
            stockObj.minimumStockLevel = minimumStockLevel;
            stockObj.maximumStockLevel = maximumStockLevel;
          } else {
            newStockLevels.push({ productId, minimumStockLevel, maximumStockLevel });
          }

          set({ additionalGeneralStockLevels: newStockLevels });
        },
        saveAdditionalGeneralStock: async (productIds: string[]) => {
          const newStockLevels = [...get().additionalGeneralStockLevels];

          const stockObjs = newStockLevels.filter(s => productIds.includes(s.productId));
          if (!stockObjs) throw new Error('Missing additional general stock objects');

          const payload = stockObjs.map(s => {
            return {
              catalogItemId: s.productId,
              minimumStockLevel: s.minimumStockLevel,
              maximumStockLevel: s.maximumStockLevel,
              stockLevelId: stockLevels[0].id
            };
          });

          await upsertAdditionalGeneralStock.mutateAsync(payload);
          await updateStockLevelStatus.mutateAsync({
            mspId: partnerId,
            customerId,
            id: stockLevels[0].id,
            state: StockLevelStatus.DRAFT
          });
          set({ additionalGeneralStockLevels: newStockLevels, status: StockLevelStatus.DRAFT });
        },
        setStatus: (newStatus: StockLevelStatus) => {
          set({ status: newStatus });
        }
      })),
    [customerId, partnerId]
  );

  return <CustomerCatalogueContext.Provider value={store}>{children}</CustomerCatalogueContext.Provider>;
};
